import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import { formatMoney, formatDateLong, formatDateWithTime, formatDateInputValue, getNairobiMonth, getNairobiYear } from '../../utils/format';
import { buildTenantMonthlyInvoiceSummary } from '../../pages/frp/frpStatements';
import LeaseAgreementModal from '../LeaseAgreementModal';
import WalletStatementExportButton from '../WalletStatementExportButton';
import Skeleton from '../Skeleton';
import PendingUnitApprovals from '../../pages/frp/PendingUnitApprovals';
import AllTransactionsButton from '../allTransactions/AllTransactionsButton';

function TenantView({ 
  user, 
  wallet, 
  pendingApprovals = [],
  onApprovalsChanged,
  onOpenAllTransactions,
  payScore,
  payScoreLoading,
  myUnits, 
  payRent, 
  showPayRent,
  fetchTenantMonthlyStatus,
  fetchTransactionStatement,
  openWaterStatement,
  tenantMonthlyStatusLoading,
  statementLoading,
}) {
  const [formerUnitBalances, setFormerUnitBalances] = useState({});
  const [currentUnitBalances, setCurrentUnitBalances] = useState({});
  const [showLeaseAgreementModal, setShowLeaseAgreementModal] = useState(false);
  const [leaseAgreementUnit, setLeaseAgreementUnit] = useState(null);
  const [lockedPropertiesMap, setLockedPropertiesMap] = useState({});
  const [actionsMenuUnitId, setActionsMenuUnitId] = useState(null);
  const [unitInfoModalUnit, setUnitInfoModalUnit] = useState(null);
  const [unitInvoices, setUnitInvoices] = useState({});
  const [showWalletTxModal, setShowWalletTxModal] = useState(false);
  const [walletTxs, setWalletTxs] = useState([]);
  const [walletTxLoading, setWalletTxLoading] = useState(false);
  const [walletTxStartDate, setWalletTxStartDate] = useState('');
  const [walletTxEndDate, setWalletTxEndDate] = useState('');
  const [walletTxFilterOpen, setWalletTxFilterOpen] = useState(false);
  const tenantTopUpPaybill = "4123905";
  const tenantTopUpAccount = String(user?.idNumber || "").trim() || "Your ID number";
  // Stays shown after the last waiting unit is answered, so the result message
  // (moved in, or declined) remains visible.
  const [approvalSectionShown, setApprovalSectionShown] = useState(pendingApprovals.length > 0);
  useEffect(() => {
    if (pendingApprovals.length > 0) setApprovalSectionShown(true);
  }, [pendingApprovals.length]);

  useEffect(() => {
    if (!actionsMenuUnitId) return undefined;

    const selector = `[data-actions-menu-root="${actionsMenuUnitId}"]`;

    const handlePointerDown = (event) => {
      if (!(event?.target instanceof Node)) return;
      if (event.target.closest(selector)) return;
      setActionsMenuUnitId(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setActionsMenuUnitId(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    const timer = setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [actionsMenuUnitId]);

  useEffect(() => {
    if (!unitInfoModalUnit) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setUnitInfoModalUnit(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [unitInfoModalUnit]);

  // Helpers to extract tenancy dates for the current user
  const getCurrentMoveInDate = (unit, currentUserId) => {
    if (!unit) return null;
    // Prefer tenantHistory entry where this user is active (no endDate)
    const activeEntry = Array.isArray(unit.tenantHistory)
      ? unit.tenantHistory.find(th => (th.tenant?._id || th.tenant) === currentUserId && !th.endDate)
      : null;
    if (activeEntry?.startDate) return activeEntry.startDate;
    // Fallbacks
    if (unit.fromDate) return unit.fromDate;
    return null;
  };

  const getFormerMoveDates = (unit, currentUserId) => {
    if (!unit) return { startDate: null, endDate: null };
    // Among history entries for this user with an endDate, pick the most recent
    const entries = Array.isArray(unit.tenantHistory)
      ? unit.tenantHistory.filter(th => (th.tenant?._id || th.tenant) === currentUserId && th.endDate)
      : [];
    if (entries.length > 0) {
      const latest = entries.reduce((a, b) => new Date(a.endDate) > new Date(b.endDate) ? a : b);
      return { startDate: latest.startDate, endDate: latest.endDate };
    }
    // Fallbacks
    return { startDate: unit.fromDate || null, endDate: unit.vacateDate || null };
  };

  const formatDate = (date) => {
    if (!date) return null;
    // Use long format without UTC adjustments for tenancy dates
    return formatDateLong(date);
  };

  // Fetch historical balances for former units
  useEffect(() => {
    const fetchFormerUnitBalances = async () => {
      if (!user || !myUnits || myUnits.length === 0) return;

      const equalId = (a, b) => {
        if (a == null || b == null) return false;
        return String(a) === String(b);
      };
      const formerUnits = myUnits.filter(unit => 
        !equalId(unit?.tenant?._id || unit?.tenant, user?._id) && 
        unit?.tenantHistory?.some(th => equalId(th?.tenant?._id || th?.tenant, user?._id))
      );

      const balances = {};
      
      for (const unit of formerUnits) {
        try {
          // Fetch the tenant-specific statement for this unit to get the last balance
          const response = await api.get(`/frp/units/${unit._id}/tenant-statements`);
          const statement = response.data.statement;

          // Backend tenant statements are now strictly per-tenant and start running
          // balances from 0 (no shared unit.balance). Get the last transaction's
          // running balance, or use summary currentBalance.
          let lastBalance = 0;
          if (statement.transactions && statement.transactions.length > 0) {
            // Transactions are sorted newest first, so take the first one's balance
            lastBalance = statement.transactions[0].runningBalance || statement.summary.currentBalance || 0;
          } else {
            lastBalance = statement.summary.currentBalance || 0;
          }

          balances[unit._id] = lastBalance;
        } catch (error) {
          console.error(`Error fetching balance for former unit ${unit._id}:`, error);
          // Fall back to unit.balance if API call fails
          balances[unit._id] = unit?.balance ? (parseFloat(unit.balance) || 0) : 0;
        }
      }
      
      setFormerUnitBalances(balances);
    };

    fetchFormerUnitBalances();
  }, [user, myUnits]);

  // Fetch lock status for properties that this tenant has access to, so that
  // locks set in the admin/manager views are respected here even if the
  // external FRP units endpoint doesn't include the flag.
  useEffect(() => {
    const loadLockedProperties = async () => {
      try {
      const resp = await api.get("/frp/properties");
      const props = Array.isArray(resp.data?.properties) ? resp.data.properties : [];
      const map = {};
      props.forEach(p => {
        const appId = p?._id || p?.id;
        const frpId = p?.frpId || p?.frp_id || p?.sourceId;
        const locked = !!(p.locked || p.managementCompany?.locked);
        if (appId) map[String(appId)] = locked;
        if (frpId) map[String(frpId)] = locked;
      });
      setLockedPropertiesMap(map);
      } catch (e) {
        // best-effort; leave map empty on error
      }
    };
    loadLockedProperties();
  }, [user]);

  // Fetch invoice rows for all units to display unpaid invoices inline
  useEffect(() => {
    const fetchUnitInvoices = async () => {
      if (!user || !myUnits || myUnits.length === 0) return;
      for (const unit of myUnits) {
        setUnitInvoices(prev => ({ ...prev, [unit._id]: { loading: true, rows: [] } }));
        try {
          const response = await api.get(`/frp/units/${unit._id}/tenant-statements`);
          const statement = response.data.statement;
          const monthly = buildTenantMonthlyInvoiceSummary(statement);
          const rows = Array.isArray(monthly?.invoiceRows) ? monthly.invoiceRows : [];
          setUnitInvoices(prev => ({ ...prev, [unit._id]: { loading: false, rows } }));
        } catch {
          setUnitInvoices(prev => ({ ...prev, [unit._id]: { loading: false, rows: [] } }));
        }
      }
    };
    fetchUnitInvoices();
  }, [user, myUnits]);

  // Fetch balances for current units using the same tenant-specific statement endpoint
  useEffect(() => {
    const fetchCurrentUnitBalances = async () => {
      if (!user || !myUnits || myUnits.length === 0) return;

      const equalId = (a, b) => {
        if (a == null || b == null) return false;
        return String(a) === String(b);
      };
      const currentUnits = myUnits.filter(unit => equalId(unit?.tenant?._id || unit?.tenant, user?._id));

      const balances = {};
      for (const unit of currentUnits) {
        try {
          const response = await api.get(`/frp/units/${unit._id}/tenant-statements`);
          const statement = response.data.statement;
          // Align with how we compute former units: use newest transaction runningBalance
          // or summary.currentBalance. Backend tenant statements are strictly per-tenant
          // and start running balances from 0 (no shared unit.balance).
          let lastBalance = 0;
          if (statement.transactions && statement.transactions.length > 0) {
            lastBalance = statement.transactions[0].runningBalance || statement.summary.currentBalance || 0;
          } else {
            lastBalance = statement.summary.currentBalance || 0;
          }
          balances[unit._id] = lastBalance;
        } catch (error) {
          console.error(`Error fetching balance for current unit ${unit._id}:`, error);
          // Fall back to unit.currentBalance (which already includes unit.balance via backend helper)
          const fallbackBalance = typeof unit.currentBalance === 'number'
            ? unit.currentBalance
            : (unit?.balance ? (parseFloat(unit.balance) || 0) : 0);
          balances[unit._id] = fallbackBalance;
        }
      }
      setCurrentUnitBalances(balances);
    };
    fetchCurrentUnitBalances();
  }, [user, myUnits]);

  // Calculate units and total balance
  const { currentUnits, formerUnits, totalBalanceDue } = useMemo(() => {
    const equalId = (a, b) => {
      if (a == null || b == null) return false;
      return String(a) === String(b);
    };
    const currentUnits = myUnits.filter(unit => equalId(unit?.tenant?._id || unit?.tenant, user?._id));
    const formerUnits = myUnits.filter(unit => !equalId(unit?.tenant?._id || unit?.tenant, user?._id) && unit?.tenantHistory?.some(th => equalId(th?.tenant?._id || th?.tenant, user?._id)));
    
    // Sum only positive balances (amounts owed). Do not net against overpayments
    const currentUnitsBalance = currentUnits.reduce((total, unit) => {
      const computed = typeof currentUnitBalances[unit._id] === 'number' ? currentUnitBalances[unit._id] : undefined;
      const fallback = typeof unit.currentBalance === 'number' ? unit.currentBalance : 0;
      const balance = computed !== undefined ? computed : fallback;
      return total + (balance > 0 ? balance : 0);
    }, 0);
    const formerUnitsBalance = formerUnits.reduce((total, unit) => {
      const balance = typeof formerUnitBalances[unit._id] === 'number' ? formerUnitBalances[unit._id] : 0;
      return total + (balance > 0 ? balance : 0);
    }, 0);
    const totalBalanceDue = currentUnitsBalance + formerUnitsBalance;
    

    return { currentUnits, formerUnits, totalBalanceDue };
  }, [myUnits, user, formerUnitBalances, currentUnitBalances]);

  const fetchWalletTransactions = async (startDate = '', endDate = '') => {
    try {
      setWalletTxLoading(true);
      const y = getNairobiYear();
      const m = getNairobiMonth();
      const defaultStart = formatDateInputValue(new Date(y, m - 2, 1));
      const defaultEnd = formatDateInputValue(new Date(y, m, 0));
      const effectiveStart = startDate || defaultStart;
      const effectiveEnd = endDate || defaultEnd;
      setWalletTxStartDate(effectiveStart);
      setWalletTxEndDate(effectiveEnd);
      const params = new URLSearchParams();
      if (effectiveStart) params.append('startDate', effectiveStart);
      if (effectiveEnd) params.append('endDate', effectiveEnd);
      const url = `/wallet/transactions${params.toString() ? `?${params}` : ''}`;
      const response = await api.get(url);
      setWalletTxs(response.data.transactions || []);
    } catch (e) {
      console.error('Error fetching wallet transactions:', e);
    } finally {
      setWalletTxLoading(false);
    }
  };

  const renderBalanceValue = (balance) => {
    if (balance > 0) {
      return <span className="text-red-600">{formatMoney(balance)}</span>;
    }
    if (balance < 0) {
      return <span className="text-green-600">-{formatMoney(Math.abs(balance))}</span>;
    }
    return <span className="text-slate-500">0.00</span>;
  };

  const formatStatusDate = (value) => {
    const formatted = formatDateWithTime(value);
    if (!formatted) return "";
    return formatted.split(" ")[0].replace(/\//g, " ");
  };

  const renderTenantUnitCard = ({
    unit,
    balance,
    moveInLabel,
    moveOutLabel = null,
    isFormer = false,
    isLocked = false,
  }) => {
    const showOutstandingPayButton = !isFormer || balance > 0;
    const showPayRentButton = showPayRent && showOutstandingPayButton;
    const statusLabel = isFormer ? "Former Rental" : "Active Rental";
    const noteText = isFormer
      ? "Deposit was held separately from rent during this tenancy. Historical statements remain available for review."
      : "Deposit does not cover rent. It is meant to restore property to original condition and the remainder is refunded to the tenant.";
    const wrapperClasses = isFormer
      ? "bg-gradient-to-br from-orange-200 via-orange-100 to-orange-50 border-orange-400/90 ring-orange-300/80 shadow-orange-200/80"
      : "bg-gradient-to-br from-emerald-200 via-emerald-100 to-emerald-50 border-emerald-400/90 ring-emerald-300/80 shadow-emerald-200/80";
    const badgeClasses = isFormer
      ? "bg-orange-50 text-orange-800 border-orange-200"
      : "bg-emerald-50 text-emerald-800 border-emerald-200";
    const dividerClasses = isFormer ? "border-orange-100" : "border-emerald-100";
    const accentText = isFormer ? "text-orange-700" : "text-emerald-700";
    const accentSoftBg = isFormer ? "bg-orange-50" : "bg-emerald-50";
    const accentRing = isFormer ? "ring-orange-100" : "ring-emerald-100";
    const accentIcon = isFormer ? "text-orange-600" : "text-emerald-600";
    const primaryButtonClasses = isFormer
      ? "bg-orange-700 hover:bg-orange-800"
      : "bg-emerald-700 hover:bg-emerald-800";
    const outlineButtonClasses = isFormer
      ? "border-orange-200 text-orange-800 hover:bg-orange-50"
      : "border-emerald-200 text-emerald-800 hover:bg-emerald-50";
    const buttonBaseClasses =
      "inline-flex items-center justify-center gap-1.5 rounded-lg px-2 sm:px-3.5 py-2 text-xs sm:text-sm font-medium transition-colors w-full sm:w-auto shadow-sm";

    const propertyTitle = `${unit.property?.name || "Unknown Property"} - ${unit.label}`;
    const propertySub =
      unit.property?.location ||
      unit.property?.address ||
      unit.property?.town ||
      unit.property?.city ||
      "";
    const openInfoModal = () => setUnitInfoModalUnit({
      unit,
      moveInLabel,
      moveOutLabel,
      balance,
      isFormer,
      noteText,
      propertySub,
      propertyTitle,
      statusLabel,
    });

    return (
  <div
    key={unit._id}
    className={`relative overflow-visible rounded-2xl border p-4 shadow-lg ring-1 backdrop-blur-sm ${actionsMenuUnitId === unit._id ? "z-40" : "z-0"} ${wrapperClasses}`}
  >
    {isLocked && (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-white/80">
        <div className="relative flex h-full w-full items-center justify-center">
          <div className="absolute inset-3 rounded-xl border-4 border-red-600" />
          <svg
            className="h-12 w-12 text-red-600 opacity-80"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-red-600 px-2.5 py-0.5 text-[11px] font-semibold text-white shadow">
            Blocked
          </div>
        </div>
      </div>
    )}

    <div className="flex items-start gap-3">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${accentSoftBg} ring-1 ${accentRing}`}>
        <svg className={`h-6 w-6 ${accentIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M7 21V5a2 2 0 012-2h6a2 2 0 012 2v16M9 7h.01M9 11h.01M9 15h.01M15 7h.01M15 11h.01M15 15h.01" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="break-words text-[15px] font-semibold leading-snug text-slate-900">
              {propertyTitle}
            </h3>

            <div className="mt-2 flex items-center justify-between gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-medium ${badgeClasses}`}>
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {statusLabel}
              </span>
            </div>
          </div>

          <div className="shrink-0 border-l border-slate-200 pl-3 text-right">
            <div className="grid gap-2">
              <div>
                <p className="text-[11px] font-medium text-slate-500">Rent</p>
                <p className={`mt-0.5 text-sm font-semibold ${accentText}`}>
                  {formatMoney(unit.rentAmount)}/mo
                </p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-slate-500">Balance</p>
                <p className="mt-0.5 text-sm font-semibold">
                  {renderBalanceValue(balance)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {(() => {
      const invoiceData = unitInvoices[unit._id];
      const unpaidRentRows = invoiceData?.rows?.filter(row => Number(row.balance || 0) > 0) ?? [];

      if (invoiceData?.loading) {
        // Hold the height the unpaid-rent rows will take, so the buttons under
        // this card do not jump once the invoices land.
        return (
          <div className="mt-3 space-y-2 px-1" aria-busy="true" aria-label="Loading invoices">
            {[0, 1].map((index) => (
              <div key={index} className="flex items-center gap-3">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 flex-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        );
      }
      if (unpaidRentRows.length === 0) return null;

      const renderInvoiceRow = (row, rowIdx) => (
        <tr
          key={row.invoiceId || `${row.date || "no-date"}-${rowIdx}`}
          className="bg-red-50/40 border-t border-gray-100"
        >
          <td className="px-1 py-1.5 font-medium text-gray-800 whitespace-nowrap overflow-hidden">
            {formatStatusDate(row.date) || "-"}
          </td>
          <td className="px-1 py-1.5 text-gray-700 truncate">
            {row.description || "Invoice"}
          </td>
          <td className="px-1 py-1.5 text-right whitespace-nowrap">
            {formatMoney(row.invoiceAmount)}
          </td>
          <td className="px-1 py-1.5 text-right whitespace-nowrap">
            {formatMoney(row.paidAmount)}
          </td>
          <td className="px-1 py-1.5 text-right font-semibold whitespace-nowrap">
            <span className="text-red-700">{formatMoney(row.balance)}</span>
          </td>
          <td className="px-1 py-1.5 text-center">
            {Number(row.paidAmount || 0) > 0 ? (
              <span className="inline-block rounded bg-yellow-100 border border-yellow-200 px-0.5 py-0.5 text-[7px] sm:text-[10px] font-semibold text-yellow-800 leading-none">Part.</span>
            ) : (
              <span className="inline-block rounded bg-red-100 border border-red-200 px-0.5 py-0.5 text-[7px] sm:text-[10px] font-semibold text-red-800 leading-none">Due</span>
            )}
          </td>
        </tr>
      );

      return (
        <div className={`mt-4 border-t pt-4 ${dividerClasses}`}>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Unpaid Invoices
          </p>
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full table-fixed text-[8px] sm:text-xs leading-tight">
              <thead className="bg-emerald-50 text-slate-600">
                <tr>
                  <th className="w-[20%] border-b border-gray-200 px-1 py-1.5 font-semibold text-left whitespace-nowrap">Date</th>
                  <th className="w-[26%] border-b border-gray-200 px-1 py-1.5 font-semibold text-left whitespace-nowrap">Description</th>
                  <th className="w-[16%] border-b border-gray-200 px-1 py-1.5 font-semibold text-right whitespace-nowrap">Invoice</th>
                  <th className="w-[13%] border-b border-gray-200 px-1 py-1.5 font-semibold text-right whitespace-nowrap">Paid</th>
                  <th className="w-[14%] border-b border-gray-200 px-1 py-1.5 font-semibold text-right whitespace-nowrap">Balance</th>
                  <th className="w-[11%] border-b border-gray-200 px-1 py-1.5 font-semibold text-center whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {unpaidRentRows.map((row, rowIdx) => renderInvoiceRow(row, rowIdx))}
              </tbody>
            </table>
          </div>
        </div>
      );
    })()}

    {!isFormer && (!wallet || wallet.balance <= 0) && (
      <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] leading-tight text-red-700">
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2v-2m0-8h2a2 2 0 012 2v4a2 2 0 01-2 2h-2m0-6h-4a1 1 0 000 2h4a1 1 0 000-2z" />
          </svg>
          <p className="font-semibold">No funds available in wallet</p>
        </div>
      </div>
    )}

    {isFormer && (
      <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-orange-800">
        <p className="font-medium">Former rental: statements remain available for review.</p>
      </div>
    )}

    <div className={`mt-4 border-t pt-4 ${dividerClasses}`}>
      <div className="flex flex-row items-center justify-end gap-1.5 sm:gap-2">
        {showPayRentButton && (
          <button
            onClick={() => {
              if (isLocked) return;
              payRent(isFormer ? { ...unit, currentBalance: balance } : unit);
            }}
            disabled={isLocked || !wallet || wallet.balance <= 0}
            className={`${buttonBaseClasses} flex-1 sm:flex-none ${
              isLocked || !wallet || wallet.balance <= 0
                ? "cursor-not-allowed bg-slate-200 text-slate-500"
                : `text-white ${primaryButtonClasses}`
            }`}
          >
            {(isLocked || !wallet || wallet.balance <= 0) && (
              <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14v2m-4-6V8a4 4 0 118 0v2m-9 9h10a1 1 0 001-1v-7a1 1 0 00-1-1H7a1 1 0 00-1 1v7a1 1 0 001 1z" />
              </svg>
            )}
            {isLocked ? "Blocked" : "Pay Rent"}
          </button>
        )}

        <button
          type="button"
          onClick={openInfoModal}
          className={`${buttonBaseClasses} flex-1 sm:flex-none border bg-white disabled:cursor-not-allowed disabled:opacity-60 ${outlineButtonClasses}`}
        >
          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 22a10 10 0 110-20 10 10 0 010 20z" />
          </svg>
          <span>Unit Info</span>
        </button>

        <div className="relative flex-1 sm:flex-none" data-actions-menu-root={unit._id}>
          <button
            type="button"
            onClick={() => {
              if (isLocked) return;
              setActionsMenuUnitId((prev) => (prev === unit._id ? null : unit._id));
            }}
            disabled={isLocked}
            className={`${buttonBaseClasses} w-full border bg-white disabled:cursor-not-allowed disabled:opacity-60 ${outlineButtonClasses}`}
            aria-haspopup="menu"
            aria-expanded={actionsMenuUnitId === unit._id}
          >
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6h.01M12 12h.01M12 18h.01" />
            </svg>
            <span>More</span>
            <svg className="ml-1 h-4 w-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {actionsMenuUnitId === unit._id && !isLocked && (
            <div
              role="menu"
              className="absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-lg border border-slate-200/80 bg-white p-1 shadow-lg ring-1 ring-slate-900/5"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionsMenuUnitId(null);
                  fetchTenantMonthlyStatus?.(unit);
                }}
                disabled={tenantMonthlyStatusLoading}
                className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg className="h-4 w-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z" />
                </svg>
                <span>Invoice Download</span>
                {tenantMonthlyStatusLoading && (
                  <span className="ml-auto inline-flex h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                )}
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionsMenuUnitId(null);
                  fetchTransactionStatement?.(unit);
                }}
                disabled={statementLoading}
                className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg className="h-4 w-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span>Statements</span>
                {statementLoading && (
                  <span className="ml-auto inline-flex h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                )}
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionsMenuUnitId(null);
                  openWaterStatement?.(unit);
                }}
                className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100"
              >
                <svg className="h-4 w-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3c3 4 6 7.2 6 10.2A6 6 0 1112 3z" />
                </svg>
                <span>Bills &amp; Invoices</span>
              </button>

              <div className="my-1 border-t border-slate-100" />

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionsMenuUnitId(null);
                  setLeaseAgreementUnit(unit);
                  setShowLeaseAgreementModal(true);
                }}
                className={`flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors focus-visible:outline-none ${
                  isFormer
                    ? "text-orange-700 hover:bg-orange-50 active:bg-orange-100/80"
                    : "text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100/80"
                }`}
              >
                <svg className={`h-4 w-4 shrink-0 ${isFormer ? "text-orange-600" : "text-emerald-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 2h8a2 2 0 012 2v18l-6-3-6 3V4a2 2 0 012-2z" />
                </svg>
                <span>Lease Agreement</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      </div>
    );
  };

  const renderUnitInfoModal = () => {
    if (!unitInfoModalUnit) return null;

    const {
      unit,
      moveInLabel,
      moveOutLabel,
      noteText,
      propertySub,
      propertyTitle,
      statusLabel,
      isFormer,
    } = unitInfoModalUnit;

    const modalAccentClasses = isFormer
      ? "border-orange-200 bg-orange-50/80 text-orange-800"
      : "border-emerald-200 bg-emerald-50/80 text-emerald-800";

    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center p-3 sm:items-center sm:p-4">
        <button
          type="button"
          aria-label="Close unit info"
          className="absolute inset-0 bg-slate-950/50"
          onClick={() => setUnitInfoModalUnit(null)}
        />

        <div className="relative w-full max-w-lg overflow-hidden rounded-[28px] bg-white shadow-2xl ring-1 ring-black/5">
          <div className={`flex items-start justify-between gap-4 border-b px-4 py-4 ${isFormer ? "bg-orange-50/70" : "bg-emerald-50/70"}`}>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Unit details</p>
              <h3 className="mt-1 truncate text-lg font-semibold text-slate-900">{propertyTitle}</h3>
              <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${modalAccentClasses}`}>
                <span className={`h-2 w-2 rounded-full ${isFormer ? "bg-orange-500" : "bg-emerald-500"}`} />
                {statusLabel}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setUnitInfoModalUnit(null)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="max-h-[80vh] space-y-3 overflow-y-auto p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className={`rounded-2xl border px-3 py-3 ${isFormer ? "border-orange-100 bg-orange-50/60" : "border-emerald-100 bg-emerald-50/60"}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Moved In</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{moveInLabel || "-"}</p>
                {moveOutLabel && <p className="mt-0.5 text-[11px] text-slate-500">Out: {moveOutLabel}</p>}
              </div>
              <div className={`rounded-2xl border px-3 py-3 ${isFormer ? "border-orange-100 bg-orange-50/60" : "border-emerald-100 bg-emerald-50/60"}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Deposit</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{formatMoney(unit.deposit)}</p>
              </div>
              <div className={`rounded-2xl border px-3 py-3 ${isFormer ? "border-orange-100 bg-orange-50/60" : "border-emerald-100 bg-emerald-50/60"}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Late Fee</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{formatMoney(unit.lateFee)}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${isFormer ? "bg-orange-50 ring-1 ring-orange-100" : "bg-emerald-50 ring-1 ring-emerald-100"}`}>
                  <svg className={`h-3.5 w-3.5 ${isFormer ? "text-orange-600" : "text-emerald-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 22a10 10 0 110-20 10 10 0 010 20z" />
                  </svg>
                </div>
                <p className="text-sm leading-6 text-slate-600">{noteText}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className={`rounded-2xl border bg-white p-3 ${isFormer ? "border-orange-100" : "border-emerald-100"}`}>
                <div className="flex items-start gap-2.5">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isFormer ? "bg-orange-50 ring-1 ring-orange-100" : "bg-emerald-50 ring-1 ring-emerald-100"}`}>
                    <svg className={`h-4 w-4 ${isFormer ? "text-orange-600" : "text-emerald-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Location</p>
                    <p className="mt-0.5 break-words text-sm font-semibold leading-5 text-slate-900">
                      {propertySub || unit.property?.name || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`rounded-2xl border bg-white p-3 ${isFormer ? "border-orange-100" : "border-emerald-100"}`}>
                <div className="flex items-start gap-2.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50 ring-1 ring-slate-100">
                    <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7a2 2 0 012-2h10a2 2 0 012 2v14M9 21V9h6v12" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Management</p>
                    {unit.property?.managementCompany ? (
                      <>
                        <p className="mt-0.5 break-words text-sm font-semibold leading-5 text-slate-900">
                          {unit.property.managementCompany.companyName}
                        </p>
                        {unit.property.managementCompany.telephoneNumber && (
                          <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-slate-600">
                            <svg className="h-3.5 w-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {unit.property.managementCompany.telephoneNumber}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm font-semibold leading-5 text-slate-900">N/A</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="max-w-2xl w-full mx-auto py-1">
      <div className="grid grid-cols-2 gap-3 mb-6">
        {/* Rent Wallet Balance — tap to open FRP wallet transactions */}
        <button
          type="button"
          onClick={() => {
            setShowWalletTxModal(true);
            fetchWalletTransactions();
          }}
          className="flex items-center gap-3 rounded-xl border border-green-200 bg-white px-3 py-3 shadow-sm text-left w-full transition-colors hover:bg-green-50 active:bg-green-100"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
            <svg className="h-5 w-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-gray-500">Wallet Balance</p>
            <p className="text-base font-bold text-green-800 leading-tight">
              {wallet?.balance != null ? formatMoney(wallet.balance) : "0.00"}
            </p>
          </div>
        </button>

        {/* Total Balance Due */}
        <div className={`flex items-center gap-3 rounded-xl border bg-white px-3 py-3 shadow-sm ${
          totalBalanceDue > 0 ? "border-red-200" : totalBalanceDue < 0 ? "border-blue-200" : "border-gray-200"
        }`}>
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            totalBalanceDue > 0 ? "bg-red-100" : totalBalanceDue < 0 ? "bg-blue-100" : "bg-gray-100"
          }`}>
            <svg className={`h-5 w-5 ${
              totalBalanceDue > 0 ? "text-red-600" : totalBalanceDue < 0 ? "text-blue-600" : "text-gray-500"
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-gray-500">Balance Due</p>
            <p className={`text-base font-bold leading-tight ${
              totalBalanceDue > 0 ? "text-red-700" : totalBalanceDue < 0 ? "text-blue-700" : "text-gray-700"
            }`}>
              {totalBalanceDue > 0
                ? formatMoney(totalBalanceDue)
                : totalBalanceDue < 0
                ? `-${formatMoney(Math.abs(totalBalanceDue))}`
                : "0.00"}
            </p>
          </div>
        </div>

      </div>
      <div className="space-y-6">
        {/* Units a manager assigned to this person. They move in only when they
            Approve & Pay here. */}
        {approvalSectionShown && (
          <div>
            {pendingApprovals.length > 0 && (
              <h3 className="text-lg font-semibold text-amber-700 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                Waiting for your approval ({pendingApprovals.length})
              </h3>
            )}
            <PendingUnitApprovals
              invitations={pendingApprovals}
              accountReference={String(user?.idNumber || "").trim()}
              onChanged={onApprovalsChanged}
            />
          </div>
        )}

        {/* Current Units Section */}
        {(() => {
          return (
            <>
              {currentUnits.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    My Current Rental Units ({currentUnits.length})
                  </h3>
                  {onOpenAllTransactions && (
                    <AllTransactionsButton onClick={onOpenAllTransactions} className="mb-3 max-w-md" />
                  )}
                  <div className="mb-3 max-w-md space-y-1 rounded-lg bg-emerald-50 border border-emerald-100 px-2 py-1.5 text-[10px] font-bold">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="text-slate-600">Paybill:</span>
                      <span className="text-[#16a34a]">{tenantTopUpPaybill}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="text-slate-600">Account:</span>
                      <span className="break-words text-emerald-600" title={tenantTopUpAccount}>
                        {tenantTopUpAccount}
                      </span>
                    </div>
                  </div>
                  <div className="grid gap-4">
                    {currentUnits.map((unit) => {
                      const uProp = unit.property || {};
                      const propId =
                        uProp._id ||
                        uProp.id ||
                        uProp.frpId ||
                        uProp.frp_id ||
                        uProp.sourceId ||
                        uProp;
                      const isLockedFromMap = propId ? !!lockedPropertiesMap[String(propId)] : false;
                      const isLocked =
                        isLockedFromMap ||
                        !!(uProp.locked || uProp.managementCompany?.locked);
                      const computedBalance = typeof currentUnitBalances[unit._id] === 'number'
                        ? currentUnitBalances[unit._id]
                        : undefined;
                      const balance = computedBalance !== undefined
                        ? computedBalance
                        : (typeof unit.currentBalance === 'number' ? unit.currentBalance : 0);
                      const moveIn = formatDate(getCurrentMoveInDate(unit, user?._id));

                      return renderTenantUnitCard({
                        unit,
                        balance,
                        moveInLabel: moveIn,
                        isLocked,
                      });
                    })}
                  </div>
                </div>
              )}

              {formerUnits.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-orange-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Former Rental Units ({formerUnits.length}) - Historical Statements Available
                  </h3>
                  {/* Someone with only former units still gets their history here. */}
                  {onOpenAllTransactions && currentUnits.length === 0 && (
                    <AllTransactionsButton onClick={onOpenAllTransactions} className="mb-3 max-w-md" />
                  )}
                  <div className="grid gap-4">
                    {formerUnits.map((unit) => {
                      const uProp = unit.property || {};
                      const propId =
                        uProp._id ||
                        uProp.id ||
                        uProp.frpId ||
                        uProp.frp_id ||
                        uProp.sourceId ||
                        uProp;
                      const isLockedFromMap = propId ? !!lockedPropertiesMap[String(propId)] : false;
                      const isLocked =
                        isLockedFromMap ||
                        !!(uProp.locked || uProp.managementCompany?.locked);
                      const balance = typeof formerUnitBalances[unit._id] === 'number'
                        ? formerUnitBalances[unit._id]
                        : 0;
                      const { startDate, endDate } = getFormerMoveDates(unit, user?._id);
                      const start = formatDate(startDate);
                      const end = formatDate(endDate);

                      return renderTenantUnitCard({
                        unit,
                        balance,
                        moveInLabel: start,
                        moveOutLabel: end,
                        isFormer: true,
                        isLocked,
                      });
                    })}
                  </div>
                </div>
              )}

              {/* Show message only if there are truly no units at all */}
              {myUnits.length === 0 && pendingApprovals.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No units assigned to you yet.</p>
                </div>
              )}
              
              {/* Show message if there are units but none are current or former for this user */}
              {myUnits.length > 0 && currentUnits.length === 0 && formerUnits.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No rental units found for your account.</p>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
    </div>
    {renderUnitInfoModal()}

    {/* FRP Wallet Transactions Modal — same as dashboard Statements */}
    {showWalletTxModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2">FRP Wallet Transactions</h3>
                <div className="text-sm opacity-90">
                  <span className="font-medium">Current FRP Wallet Balance: {formatMoney(wallet?.balance || 0)}</span>
                </div>
              </div>
              <button
                onClick={() => { setShowWalletTxModal(false); setWalletTxStartDate(''); setWalletTxEndDate(''); }}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <WalletStatementExportButton accountName={user?.name} startDate={walletTxStartDate} endDate={walletTxEndDate} disabled={walletTxLoading} />
          {/* Date Filter */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            <button
              onClick={() => setWalletTxFilterOpen(prev => !prev)}
              className="w-full flex items-center justify-between text-gray-800 font-medium"
            >
              <span>Filter</span>
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={walletTxFilterOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
              </svg>
            </button>
            {walletTxFilterOpen && (
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                <div className="flex-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={walletTxStartDate} onChange={e => setWalletTxStartDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={walletTxEndDate} onChange={e => setWalletTxEndDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button onClick={() => fetchWalletTransactions(walletTxStartDate, walletTxEndDate)}
                    className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm">
                    Filter
                  </button>
                  <button onClick={() => { setWalletTxStartDate(''); setWalletTxEndDate(''); fetchWalletTransactions(); }}
                    className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm">
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6">
              {walletTxLoading ? (
                <div className="text-center py-8"><p className="text-gray-500">Loading transactions...</p></div>
              ) : walletTxs.length === 0 ? (
                <div className="text-center py-8"><p className="text-gray-500 text-sm">No transactions found.</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 min-w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-24 sm:w-28">Date</th>
                        <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 min-w-0">Description</th>
                        <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-20 sm:w-24">Amount</th>
                        <th className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-700 w-20 sm:w-24">New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletTxs.map((tx, index) => {
                        const hasBalanceSnapshot = tx?.balanceAfter !== null &&
                          tx?.balanceAfter !== undefined &&
                          Number.isFinite(Number(tx.balanceAfter));
                        let balanceAfter = hasBalanceSnapshot ? Number(tx.balanceAfter) : wallet?.balance || 0;
                        if (!hasBalanceSnapshot) {
                          for (let i = 0; i < index; i++) balanceAfter -= walletTxs[i].amount;
                        }
                        return (
                          <tr key={tx._id || index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm whitespace-normal break-words">
                              {formatDateWithTime(tx.timestamp)}
                            </td>
                            <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm">
                              <div className="break-words max-w-xs sm:max-w-md">{tx.description}</div>
                            </td>
                            <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                              {tx.amount >= 0
                                ? <span className="text-green-600">+{formatMoney(tx.amount)}</span>
                                : <span className="text-red-600">{formatMoney(tx.amount)}</span>}
                            </td>
                            <td className="border border-gray-300 px-1 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                              {formatMoney(balanceAfter)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    <LeaseAgreementModal
      open={showLeaseAgreementModal}
      onClose={() => { setShowLeaseAgreementModal(false); setLeaseAgreementUnit(null); }}
      selectedUnit={leaseAgreementUnit}
      properties={[]}
      managementCompanies={[]}
      selectedManagementCompany={null}
      user={user}
    />
    </>
  );
}

export default TenantView;
